import React, { useState } from 'react';
import api from '../api/api';
import Icon from '../components/Icon';
import { useBilling } from './BillingContext';
import { formatLongDate, formatMoney } from './format';

const REASONS = [
    { id: 'too_expensive', label: "It's too expensive" },
    { id: 'not_using', label: "I'm not posting enough to use it" },
    { id: 'results', label: "Posts aren't bringing results yet" },
    { id: 'missing_feature', label: "It's missing something I need" },
    { id: 'too_complex', label: "It's hard to use" },
    { id: 'switching', label: "I'm switching to another tool" },
    { id: 'other', label: 'Something else' },
];

// One offer, matched to why they're leaving. null when there's nothing to offer.
const offerFor = (reason, account, pricing) => {
    if (!account?.exitOfferAvailable) return null;
    const plan = pricing?.plans.find(p => p.id === account.subscribedPlan);
    const yearly = account.interval === 'year';
    const price = plan && (yearly ? plan.price.year : plan.price.month);
    const discounted = price && formatMoney(Math.round(price / 2 * 100) / 100, pricing.currency);
    const discountOffer = {
        id: 'discount',
        icon: 'sell',
        title: yearly ? 'Take 50% off your next renewal' : 'Take 50% off for the next 3 months',
        body: price ? `${discounted} instead of ${formatMoney(price, pricing.currency)} ${yearly ? 'for your next year' : 'a month'}. Your plans, links and brand voice stay exactly as they are.` : 'Keep everything you have set up at half the price.',
        accept: 'Claim 50% off',
    };

    if (account.status === 'trialing') {
        return {
            id: 'extend_trial',
            icon: 'more_time',
            title: 'Take 7 more days, free',
            body: 'Results from content take a couple of weeks to show. Keep your trial going and see what your first full week of posts brings in.',
            accept: 'Extend my trial',
        };
    }
    if (reason === 'too_expensive' && account.subscribedPlan === 'scale') {
        const growth = pricing?.plans.find(p => p.id === 'growth');
        return {
            id: 'downgrade',
            icon: 'south',
            title: 'Move to Growth instead',
            body: growth
                ? `${formatMoney(yearly ? growth.price.yearPerMonth : growth.price.month, pricing.currency)} a month for up to 3 products and 400 generations. Keep your weekly plan running for less.`
                : 'Keep your weekly plan running for less.',
            accept: 'Switch to Growth',
        };
    }
    if (reason === 'not_using') {
        return {
            id: 'pause',
            icon: 'pause_circle',
            title: 'Pause for a month instead',
            body: "Busy stretch? Pause billing for 30 days. Your products, ICPs and plans are saved, and it resumes on its own. You'll be on Free limits meanwhile.",
            accept: 'Pause for 30 days',
        };
    }
    if (reason === 'switching') return null;
    return discountOffer;
};

const CancelFlow = ({ onClose }) => {
    const { account, pricing, applyAccount } = useBilling();
    const [step, setStep] = useState('reason');
    const [reason, setReason] = useState('');
    const [details, setDetails] = useState('');
    const [isBusy, setIsBusy] = useState(false);
    const [error, setError] = useState('');
    const [result, setResult] = useState(null);

    const offer = offerFor(reason, account, pricing);
    const endDate = formatLongDate(account?.status === 'trialing' ? account.trialEnd : account?.currentPeriodEnd);

    const act = async (action, done) => {
        setIsBusy(true);
        setError('');
        try {
            const response = await action();
            applyAccount(response.account);
            setResult(done);
            setStep('done');
        } catch (e) {
            setError(e.response?.data?.error || 'Something went wrong. Please try again.');
        } finally {
            setIsBusy(false);
        }
    };

    const acceptOffer = () => act(() => api.acceptRetentionOffer(offer.id), {
        icon: 'celebration',
        title: {
            discount: "Done: 50% off is applied",
            extend_trial: 'Your trial is 7 days longer',
            pause: 'Your plan is paused for 30 days',
            downgrade: "You're on Growth now",
        }[offer.id],
        body: offer.id === 'pause' ? 'Billing picks back up on its own after 30 days. You can resume early from this page.' : 'Thanks for staying. Your weekly plan keeps running.',
    });

    const cancel = () => act(() => api.cancelPlan({ reason, details }), {
        icon: 'check_circle',
        title: 'Your plan is cancelled',
        body: `You keep everything until ${endDate}, then move to Free. You can change your mind any time before then.`,
    });

    return (
        <div className="ls-modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && !isBusy && onClose()}>
            <div className="ls-modal ls-cancel" role="dialog" aria-modal="true" aria-labelledby="cancel-title" style={{ width: 'min(520px, 100%)' }}>
                {step === 'reason' && (
                    <>
                        <h2 id="cancel-title" className="ls-modal-title">Before you go, what's the main reason?</h2>
                        <div className="ls-muted" style={{ fontSize: 14, marginTop: -6 }}>It helps us make light speed better.</div>
                        <div className="ls-reasons" role="radiogroup">
                            {REASONS.map(item => (
                                <label key={item.id} className={`ls-reason${reason === item.id ? ' is-selected' : ''}`}>
                                    <input type="radio" name="reason" value={item.id} checked={reason === item.id} onChange={() => setReason(item.id)} />
                                    {item.label}
                                </label>
                            ))}
                        </div>
                        {reason && (
                            <textarea
                                className="ls-textarea is-compact"
                                rows={3}
                                placeholder={reason === 'missing_feature' ? 'What would you need?' : 'Anything else we should know? (optional)'}
                                value={details}
                                onChange={(e) => setDetails(e.target.value)}
                            />
                        )}
                        <div className="ls-modal-actions">
                            <button type="button" className="ls-btn ls-btn-secondary" onClick={onClose}>Keep my plan</button>
                            <button type="button" className="ls-btn ls-btn-ghost" disabled={!reason} onClick={() => setStep(offer ? 'offer' : 'confirm')}>
                                Continue
                            </button>
                        </div>
                    </>
                )}

                {step === 'offer' && offer && (
                    <>
                        <div className="ls-offer">
                            <div className="ls-upgrade-icon"><Icon name={offer.icon} size={24} /></div>
                            <h2 id="cancel-title" className="ls-modal-title">{offer.title}</h2>
                            <p>{offer.body}</p>
                        </div>
                        {error && <div className="ls-error" role="alert"><Icon name="error" />{error}</div>}
                        <button type="button" className="ls-btn ls-btn-primary is-large" disabled={isBusy} onClick={acceptOffer}>
                            {isBusy ? <span className="ls-spinner" /> : offer.accept}
                        </button>
                        <button type="button" className="ls-link is-muted" style={{ alignSelf: 'center' }} disabled={isBusy} onClick={() => setStep('confirm')}>
                            No thanks, continue cancelling
                        </button>
                    </>
                )}

                {step === 'confirm' && (
                    <>
                        <h2 id="cancel-title" className="ls-modal-title">Cancel your plan?</h2>
                        <ul className="ls-cancel-list">
                            <li><Icon name="event" size={18} />You keep everything until {endDate || 'the end of this period'}.</li>
                            <li><Icon name="south" size={18} />Then you move to Free: 1 product and 20 generations a month.</li>
                            <li><Icon name="inventory_2" size={18} />Your products, ICPs, plans and links are kept.</li>
                        </ul>
                        {error && <div className="ls-error" role="alert"><Icon name="error" />{error}</div>}
                        <div className="ls-modal-actions">
                            <button type="button" className="ls-btn ls-btn-primary" disabled={isBusy} onClick={onClose}>Keep my plan</button>
                            <button type="button" className="ls-btn ls-btn-ghost" disabled={isBusy} onClick={cancel}>
                                {isBusy ? <span className="ls-spinner" /> : 'Cancel plan'}
                            </button>
                        </div>
                    </>
                )}

                {step === 'done' && result && (
                    <>
                        <div className="ls-offer">
                            <div className="ls-upgrade-icon"><Icon name={result.icon} size={24} /></div>
                            <h2 id="cancel-title" className="ls-modal-title">{result.title}</h2>
                            <p>{result.body}</p>
                        </div>
                        <button type="button" className="ls-btn ls-btn-secondary" style={{ alignSelf: 'center' }} onClick={onClose}>Done</button>
                    </>
                )}
            </div>
        </div>
    );
};

export default CancelFlow;
